/**
 * Standard tournament draw position configurations
 * These define the bracket positioning for different draw sizes
 */

// Draw position configuration interface
export interface DrawConfig {
  positions: number[];
  groups: string[];
}

/**
 * Hardcoded tournament draw positions for standard bracket sizes
 * Each configuration defines:
 * - positions: The seed numbers in bracket order (position 1 = top of bracket)
 * - groups: Seeding groups for each position (1, 2, 3-4, 5-8, etc.)
 */
export const DRAW_POSITION_CONFIGS: Record<number, DrawConfig> = {
  2: {
    positions: [1, 2],
    groups: ['1', '2'],
  },
  4: {
    positions: [1, 4, 3, 2],
    groups: ['1', '2', '3', '4'],
  },
  8: {
    positions: [1, 8, 5, 4, 3, 6, 7, 2],
    groups: ['1', '2', '3', '4', '5-8', '5-8', '5-8', '5-8'],
  },
  16: {
    positions: [1, 16, 9, 8, 5, 12, 13, 4, 3, 14, 11, 6, 7, 10, 15, 2],
    groups: [
      '1',
      '2',
      '3',
      '4',
      '5-8',
      '5-8',
      '5-8',
      '5-8',
      '9-12',
      '9-12',
      '9-12',
      '9-12',
      '13-16',
      '13-16',
      '13-16',
      '13-16',
    ],
  },
  32: {
    positions: [
      1, 32, 17, 16, 9, 24, 25, 8, 5, 28, 21, 12, 13, 20, 29, 4, 3, 30, 19, 14, 11, 22, 27, 6, 7,
      26, 23, 10, 15, 18, 31, 2,
    ],
    groups: [
      '1',
      '2',
      '3',
      '4',
      '5-8',
      '5-8',
      '5-8',
      '5-8',
      '9-12',
      '9-12',
      '9-12',
      '9-12',
      '13-16',
      '13-16',
      '13-16',
      '13-16',
      '17-20',
      '17-20',
      '17-20',
      '17-20',
      '21-24',
      '21-24',
      '21-24',
      '21-24',
      '25-28',
      '25-28',
      '25-28',
      '25-28',
      '29-32',
      '29-32',
      '29-32',
      '29-32',
    ],
  },
  64: {
    positions: [
      1, 64, 33, 32, 17, 48, 49, 16, 9, 56, 41, 24, 25, 40, 57, 8, 5, 60, 37, 28, 21, 44, 53, 12,
      13, 52, 45, 20, 29, 36, 61, 4, 3, 62, 35, 30, 19, 46, 51, 14, 11, 54, 43, 22, 27, 38, 59, 6,
      7, 58, 39, 26, 23, 42, 55, 10, 15, 50, 47, 18, 31, 34, 63, 2,
    ],
    groups: [
      '1',
      '2',
      '3',
      '4',
      '5-8',
      '5-8',
      '5-8',
      '5-8',
      '9-12',
      '9-12',
      '9-12',
      '9-12',
      '13-16',
      '13-16',
      '13-16',
      '13-16',
      '17-20',
      '17-20',
      '17-20',
      '17-20',
      '21-24',
      '21-24',
      '21-24',
      '21-24',
      '25-28',
      '25-28',
      '25-28',
      '25-28',
      '29-32',
      '29-32',
      '29-32',
      '29-32',
      '33-36',
      '33-36',
      '33-36',
      '33-36',
      '37-40',
      '37-40',
      '37-40',
      '37-40',
      '41-44',
      '41-44',
      '41-44',
      '41-44',
      '45-48',
      '45-48',
      '45-48',
      '45-48',
      '49-52',
      '49-52',
      '49-52',
      '49-52',
      '53-56',
      '53-56',
      '53-56',
      '53-56',
      '57-60',
      '57-60',
      '57-60',
      '57-60',
      '61-64',
      '61-64',
      '61-64',
      '61-64',
    ],
  },
  128: {
    positions: [
      1, 128, 65, 64, 33, 96, 97, 32, 17, 112, 81, 48, 49, 80, 113, 16, 9, 120, 73, 56, 41, 88, 105,
      24, 25, 104, 89, 40, 57, 72, 121, 8, 5, 124, 69, 60, 37, 92, 101, 28, 21, 108, 85, 44, 53, 76,
      117, 12, 13, 116, 77, 52, 45, 84, 109, 20, 29, 100, 93, 36, 61, 68, 125, 4, 3, 126, 67, 62,
      35, 94, 99, 30, 19, 110, 83, 46, 51, 78, 115, 14, 11, 118, 75, 54, 43, 86, 107, 22, 27, 102,
      91, 38, 59, 70, 123, 6, 7, 122, 71, 58, 39, 90, 103, 26, 23, 106, 87, 42, 55, 74, 119, 10, 15,
      114, 79, 50, 47, 82, 111, 18, 31, 98, 95, 34, 63, 66, 127, 2,
    ],
    groups: [
      '1',
      '2',
      '3',
      '4',
      '5-8',
      '5-8',
      '5-8',
      '5-8',
      '9-12',
      '9-12',
      '9-12',
      '9-12',
      '13-16',
      '13-16',
      '13-16',
      '13-16',
      '17-20',
      '17-20',
      '17-20',
      '17-20',
      '21-24',
      '21-24',
      '21-24',
      '21-24',
      '25-28',
      '25-28',
      '25-28',
      '25-28',
      '29-32',
      '29-32',
      '29-32',
      '29-32',
      '33-36',
      '33-36',
      '33-36',
      '33-36',
      '37-40',
      '37-40',
      '37-40',
      '37-40',
      '41-44',
      '41-44',
      '41-44',
      '41-44',
      '45-48',
      '45-48',
      '45-48',
      '45-48',
      '49-52',
      '49-52',
      '49-52',
      '49-52',
      '53-56',
      '53-56',
      '53-56',
      '53-56',
      '57-60',
      '57-60',
      '57-60',
      '57-60',
      '61-64',
      '61-64',
      '61-64',
      '61-64',
      '65-68',
      '65-68',
      '65-68',
      '65-68',
      '69-72',
      '69-72',
      '69-72',
      '69-72',
      '73-76',
      '73-76',
      '73-76',
      '73-76',
      '77-80',
      '77-80',
      '77-80',
      '77-80',
      '81-84',
      '81-84',
      '81-84',
      '81-84',
      '85-88',
      '85-88',
      '85-88',
      '85-88',
      '89-92',
      '89-92',
      '89-92',
      '89-92',
      '93-96',
      '93-96',
      '93-96',
      '93-96',
      '97-100',
      '97-100',
      '97-100',
      '97-100',
      '101-104',
      '101-104',
      '101-104',
      '101-104',
      '105-108',
      '105-108',
      '105-108',
      '105-108',
      '109-112',
      '109-112',
      '109-112',
      '109-112',
      '113-116',
      '113-116',
      '113-116',
      '113-116',
      '117-120',
      '117-120',
      '117-120',
      '117-120',
      '121-124',
      '121-124',
      '121-124',
      '121-124',
      '125-128',
      '125-128',
      '125-128',
      '125-128',
    ],
  },
};

/**
 * Get supported tournament draw sizes
 */
export const SUPPORTED_DRAW_SIZES = Object.keys(DRAW_POSITION_CONFIGS)
  .map(Number)
  .sort((a, b) => a - b);

/**
 * Get draw positions based on seed number and draw size.
 * This is the hardcoded seeding function that defines standard bracket positioning.
 *
 * @param drawSize - The maximum draw size (group capacity)
 * @param actualPlayerCount - The actual number of players (can be less than drawSize for partial draws)
 */
export function getDrawPositions(drawSize: number, actualPlayerCount?: number): DrawConfig {
  const config = DRAW_POSITION_CONFIGS[drawSize];
  if (!config) {
    throw new Error(
      `Unsupported draw size: ${drawSize}. Supported sizes: ${SUPPORTED_DRAW_SIZES.join(', ')}`,
    );
  }

  // If we have fewer players than the draw size, we need to adjust the positions
  const playerCount = actualPlayerCount || drawSize;

  if (playerCount < drawSize) {
    // For partial draws, we only use the first N positions from the full draw
    return {
      positions: config.positions.slice(0, playerCount),
      groups: config.groups.slice(0, playerCount),
    };
  }

  return config;
}
